// src/components/trust/TrustBadgesStrip.jsx
import React from "react";

/**
 * TrustBadgesStrip – „Unternehmen, die uns vertrauen“-Stil mit Vertrauenssiegeln
 * - Breiter, hochwertiger Abschnitt mit großzügigem vertikalem Abstand
 * - Marquee-Scroll bei normaler Bewegung; statisches Grid bei Reduced Motion
 * - Icons + prägnante Labels (Versichert, Umweltfreundlich, Pünktlich, Bewertungen usw.)
 */

export default function TrustBadgesStrip({
  title = "Vertrauenswürdig für Qualität & Zuverlässigkeit",
  subtitle = "Unsere Zusagen, auf die sich Unternehmen in ganz Deutschland verlassen.",
  badges = DEFAULT_BADGES,        // [{label, Icon}]
  padY = "py-16 sm:py-20",        // Klassen für vertikalen Abstand
  className = "",
}) {
  const reduced = usePrefersReducedMotion();
  const track = React.useMemo(() => [...badges, ...badges], [badges]); // nahtlose Schleife

  return (
    <section className={[" bg-white", padY, className].join(" ")}>
      {/* weicher Trenner oben */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-8 bg-gradient-to-b from-emerald-50/60 to-transparent" aria-hidden />

      <div className="mx-auto max-w-6xl px-6 lg:px-12">
        <header className="mb-8 text-center">
          <h3 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-gray-900">
            {title}
          </h3>
          {subtitle && (
            <p className="mt-3 text-sm sm:text-base text-gray-600">{subtitle}</p>
          )}
        </header>

        {!reduced ? (
          // Marquee-Fluss
          <div className="relative overflow-hidden">
            {/* Kantenverläufe */}
            <div className="pointer-events-none absolute inset-y-0 left-0 w-12 bg-gradient-to-r from-white to-transparent" />
            <div className="pointer-events-none absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-white to-transparent" />

            <ul className="flex items-center gap-8 sm:gap-12 animate-badges-track will-change-transform">
              {track.map((b, i) => (
                <li key={i} className="shrink-0">
                  <BadgePill Icon={b.Icon} label={b.label} />
                </li>
              ))}
            </ul>
          </div>
        ) : (
          // Reduced Motion: statisches, SEO-freundliches Grid
          <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4 sm:gap-6 place-items-center">
            {badges.map((b, i) => (
              <li key={i}>
                <BadgePill Icon={b.Icon} label={b.label} />
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* lokales CSS für Marquee */}
      <style>{`
        @keyframes badgesTrack {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-badges-track {
          animation: badgesTrack 28s linear infinite;
        }
        @media (max-width: 640px) {
          .animate-badges-track { animation-duration: 5s; }
        }
        @media (prefers-reduced-motion: reduce) {
          .animate-badges-track { animation: none; }
        }
      `}</style>
    </section>
  );
}

function BadgePill({ Icon, label }) {
  return (
    <div
      className={[
        "inline-flex items-center gap-2 sm:gap-2.5",
        "rounded-2xl border border-gray-200 bg-white",
        "px-3.5 py-2 sm:px-4 sm:py-2.5",
        "shadow-sm transition-all hover:shadow-md hover:-translate-y-0.5",
      ].join(" ")}
    >
      <span className="grid place-items-center rounded-xl bg-emerald-50 border border-emerald-100 h-8 w-8 sm:h-9 sm:w-9">
        <Icon className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-600" />
      </span>
      <span className="text-sm sm:text-base font-semibold text-gray-900">{label}</span>
    </div>
  );
}

/* Hook für Reduced Motion */
function usePrefersReducedMotion() {
  const [reduced, setReduced] = React.useState(false);
  React.useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const on = () => setReduced(!!mq.matches);
    on();
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  return reduced;
}

/* Minimale Inline-Icons */
function ShieldIcon({ className = "" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M12 3l8 3v6c0 5-3.5 8-8 10C7.5 20 4 17 4 12V6l8-3z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}
function LeafIcon({ className = "" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M4 20c9 0 16-7 16-16 0 0-7-2-14 5 0 0 5 0 7-2" />
    </svg>
  );
}
function ClockIcon({ className = "" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 3" />
    </svg>
  );
}
function StarIcon({ className = "" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12 17.3l6.2 3.6-1.7-7.2L21 9.3l-7.2-.6L12 2 10.2 8.7 3 9.3l4.5 4.4-1.7 7.2 6.2-3.6z" />
    </svg>
  );
}
function RibbonIcon({ className = "" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <circle cx="12" cy="8" r="4" />
      <path d="M8 12l-2 8 6-3 6 3-2-8" />
    </svg>
  );
}
function ShieldCheckIcon({ className = "" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M12 3l8 3v6c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6l8-3z" />
      <path d="M8.5 12.5l2.2 2.2 4.3-4.3" />
    </svg>
  );
}

/* Standard-Labels für eine Reinigungsfirma */
const DEFAULT_BADGES = [
  { label: "Versichert & abgesichert", Icon: ShieldIcon },
  { label: "Umweltfreundliche Produkte", Icon: LeafIcon },
  { label: "Pünktliche Teams", Icon: ClockIcon },
  { label: "4,9★ Verifizierte Bewertungen", Icon: StarIcon },
  { label: "Zufriedenheitsgarantie", Icon: RibbonIcon },
  { label: "Hintergrundgeprüftes Personal", Icon: ShieldCheckIcon },
];
